// src/pages/markets/board/card/EntryMarkerPanel.tsx
//
// The position line is the only new thing here; everything under it is the
// trader stats card the event view's trades sidebar already opens.

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { EntryMarker } from '../utils/entryMarkers';
import type { SideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { TraderDetail } from './TraderDetail';
import { usePositions } from '../../api/usePositions';
import {
    formatAge,
    formatCents,
    formatDuration,
    formatUsd,
} from './cardFormat';
import { formatPnlShort } from '../../utils/traderFormat';
import { numSx } from '../../../../components/common/page/numberStyles';
import { PANEL_PAD_X } from './panelParts';

const PANEL_WIDTH = 288;

export interface EntryPositionPnlProps {
    address: string;
    eventId: string;
    marketId: string;
    side: 'YES' | 'NO';
}

export function EntryPositionPnl({
    address,
    eventId,
    marketId,
    side,
}: EntryPositionPnlProps) {
    const { positions, loading } = usePositions(address, {
        sort: 'recent',
        sortDir: 'desc',
        page: 0,
        pageSize: 10,
        eventId,
        side,
    });

    if (loading) {
        return (
            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                loading P&amp;L…
            </Typography>
        );
    }

    const pos = positions.find(p => p.condition_id === marketId) ?? null;
    if (!pos) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                flexWrap: 'wrap',
            }}
        >
            <Typography
                sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: pos.pnl >= 0 ? 'success.main' : 'error.main',
                }}
            >
                {formatPnlShort(pos.pnl)}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {formatPnlShort(pos.realized_pnl)} realized ·{' '}
                {formatPnlShort(pos.unrealized_pnl)} unrealized
            </Typography>
        </Box>
    );
}

export interface EntryMarkerPanelProps {
    marker: EntryMarker;
    labels: SideLabels;
    marketId: string;
    eventId: string;
    now: number;
}

export function EntryMarkerPanel({
    marker,
    labels,
    marketId,
    eventId,
    now,
}: EntryMarkerPanelProps) {
    const theme = useTheme();
    const side = marker.side === 'NO' ? labels.no : labels.yes;

    return (
        <Box sx={{ width: PANEL_WIDTH }}>
            <Box
                sx={{
                    px: PANEL_PAD_X,
                    py: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                }}
            >
                <Typography
                    title={side.title}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        ...numSx,
                        color: sideColor(labels, marker.side, theme),
                    }}
                >
                    {formatUsd(marker.capitalUsd)} {side.text}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        ...numSx,
                    }}
                >
                    entered {formatCents(marker.entryPrice)} · held{' '}
                    {formatDuration(marker.heldSeconds)} · changed{' '}
                    {formatAge(marker.lastChangeAt, now)}
                </Typography>
                <EntryPositionPnl
                    address={marker.address}
                    eventId={eventId}
                    marketId={marketId}
                    side={marker.side}
                />
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <TraderDetail address={marker.address} />
            </Box>
        </Box>
    );
}
